<template>
  <div ref="VIREMENT_SALARIE">
    <section-header v-if="!hideTitle"
                    ref="CI_TRANSACTION_LIST"
                    :ressource="ressource"
                    :is-extended="extended"
                    :dialog="dialog"
                    :fiche=fiche
                    @extended=setExtended
                    @close-dialog=closeSection>
    </section-header>

    <v-row class="py-10 m-0">
      <v-col>
        <v-combobox ref="COMBO_BOX"
                    v-model=etablissementSelected
                    class="'edit-field'"
                    :items="etablissementList"
                    :label=labelEtablissement
                    :placeholder="placeholder"
                    :persistent-placeholder="placeholder!=''"
                    :outlined="false"
                    :ripple="false"
                    @click="onFocus()"
                    dense>
        </v-combobox>
      </v-col>
    </v-row>

    <div v-if="extended">
      <v-data-table :height="listHeight"
                    dense fixed-header
                    :headers="headers"
                    :items="items"
                    :items-per-page="-1"
                    :must-sort="true"
                    :mobile-breakpoint="100"
                    item-key="transactionId"
                    class="white"
                    no-data-text="Aucune demande"
                    :disable-pagination="true"
                    :hide-default-footer="true"
                    @click:row="openAttestation">
        <template #[`item.etablissement`]="{ item }">
          {{ etablissement(item.etablissementId) }}
        </template>
        <template #[`item.client`]="{ item }">
          {{ client(item.etablissementId) }}
        </template>
        <template #[`item.organisme`]="{ item }">
          {{ organisme(item.attestationDefId) }}
        </template>
        <template #[`item.dateDemande`]="{ item }">
          {{ formatDate(item.dateDemande) }}
        </template>
        <template #[`item.dateStatut`]="{ item }">
          {{ formatDate(item.dateStatut) }}
        </template>
        <template #[`item.dateValid`]="{ item }">
          {{ formatDate(item.dateValid) }}
        </template>
        <template #[`item.statut`]="{ item }">
          {{ statut(item.statut) }}
        </template>
      </v-data-table>
    </div>
    <section-container v-if="showSection"
                       :dialogOpen="showSection"
                       :extended="true"
                       :fullscreen="section.fullscreen"
                       :section="section"
                       @close-dialog="closeAttestation"
                       @updated="loadTable">
    </section-container>
  </div>
</template>
<script lang="ts">
  import { GenSection, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import SectionContainer from '@/framework/base/components/SectionContainer.vue';
  import Section from '@/framework/base/classes/Section';
  import { FwkButtons, RessourceFwkButton } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Etablissement from '@/generated/classes/Etablissement';
  import ListData, { ListFilter, ColumnFilter, ListChoix } from '@/generated/classes/ListData';
  import Item from '@/framework/base/classes/Item';
  import { Extended } from '@/framework/base/classes/Schema';
  import { DateFormat } from '@/framework/base/helpers/DateFormat';
  import { getCurrency } from '@/framework/base/helpers/Localization';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { defineComponent } from "vue";
  import Attestation from '@/extend/classes/Attestation';
  import { AttestationStatuts } from '@/generated/classes/Attestation';
  import AttestationDef from '@/generated/classes/AttestationDef';
  import SectionDemandeAttestation from '@/extend/sections/SectionDemandeAttestationExtend.vue';
  import { storeAttestation } from '@/generated/stores';

  export default defineComponent({
    name: 'SectionTransactionSalarie',
    components: {
      SectionHeader,
      LoaderButton,
      SectionContainer
    },
    props: {
      hideTitle: {
        type: Boolean,
        required: false,
      },
      extended: {
        type: Boolean,
        required: false,
      },
      dialog: {
        type: Boolean,
        required: false,
      },
      fiche: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        newLoading: false,
        placeholder: 'Sélectionner',
        listHeight: 'calc(100vh - 500px)',
        attestationDefList: [] as AttestationDef[],
        attestationList: [] as Attestation[],
        etablissementId: new Item(-1, 'Demande en traitement'),
        etablissementList: [] as Item[],
        labelEtablissement: 'Etablissement',
        section: new Section(),
        showSection: false,
        isFocusing: false
      };
    },
    computed: {
      ressource(): any {
        return RessourceGenSection(GenSection.ATTESTATION_COMPTE);
      },
      generateLabel(): any {
        return RessourceFwkButton(FwkButtons.GENERER_VIREMENT).label;
      },
      headers(): any {
        return [
          { title: "Client", value: 'client', cellClass: '', sortable: true },
          { title: "Etablissement", value: 'etablissement', cellClass: '', sortable: true },
          { title: "Organisme", value: 'organisme', cellClass: '', sortable: true },
          { title: "Date demande", value: 'dateDemande', cellClass: '', sortable: true, align: 'right' },
          { title: "Date mise à jour", value: 'dateStatut', cellClass: '', sortable: true, align: 'right' },
          { title: "Statut", value: 'statut', cellClass: '', sortable: true, align: 'right' },
          { title: "Date validité", value: 'dateValid', cellClass: '', sortable: true, align: 'right' }
        ];
      },
      items(): Attestation[] {
        return this.attestationList;
      },
      searchDisabled(): boolean {
        return false;
      },
      etablissementSelected: {
        get(): Item {
          return this.etablissementId;
        },
        set(item: Item) {
          this.etablissementId = item;
          this.loadTable();
        },
      },
    },
    mounted() {
      AttestationDef.list(new ListFilter()).then((listData: ListData) => {
        this.attestationDefList = listData.reccords as AttestationDef[];
      });
      Etablissement.list(new ListFilter()).then((listData: ListData) => {
        const res: Item[] = [];
        res.push(new Item(-1, 'Demandes à traiter'));
        const etablissements = listData.reccords as Etablissement[];
        etablissements.forEach((etablissement) => {
          if (etablissement.clientId > 0) {
            const label = etablissement.clientId.toString() + " - " + etablissement.alias;
            res.push(new Item(etablissement.id, label));
          }
        })
        this.etablissementList = res;
      })
      this.loadTable();
    },
    methods: {
      onFocus() {
        if (this.isFocusing) return;
        this.isFocusing = true;

        setTimeout(() => {
          const comboBox = this.$refs.COMBO_BOX;
          const inputElement = comboBox?.$el?.querySelector('input');
          if (inputElement) {
            inputElement.select();
          }
          this.isFocusing = false;
        }, 50);
      },
      loadTable(): void {
        this.newLoading = true;
        this.attestationList = [];
        const filter = new ListFilter();
        filter.pageLimit = -1;
        const columnFilter = new ColumnFilter();
        if (this.etablissementId.key == undefined || this.etablissementId.key == -1) {
          columnFilter.name = "statut";
          columnFilter.staticFilter = true;
          {
            const choix = new ListChoix();
            choix.value = AttestationStatuts.DEMANDE;
            columnFilter.filters.push(choix);
          }
          {
            const choix = new ListChoix();
            choix.value = AttestationStatuts.PRIS_EN_CHARGE;
            columnFilter.filters.push(choix);
          }
          {
            const choix = new ListChoix();
            choix.value = AttestationStatuts.REFUSE;
            columnFilter.filters.push(choix);
          }
        } else {
          columnFilter.name = "etablissementId";
          columnFilter.staticFilter = true;
          const choix = new ListChoix();
          choix.value = this.etablissementId.key;
          columnFilter.filters.push(choix);
        }
        filter.columnsFilter.push(columnFilter);
        Attestation.list(filter).then((listData: ListData) => {
          this.attestationList = listData.reccords as Attestation[];
          this.newLoading = false;
        }).catch(() => {
          this.newLoading = false;
        });
      },
      etablissement(value: number) {
        const label = this.etablissementList.find(a => a.key == value)?.title.split(' - ')[1];
        return label;
      },
      client(value: number) {
        const label = this.etablissementList.find(a => a.key == value)?.title.split(' - ')[0];
        return label;
      },
      organisme(value: number) {
        const def = this.attestationDefList.find(a => a.id == value);
        return def?.organisme + ' ' + def?.caisse;
      },
      statut(value: AttestationStatuts) {
        return Attestation.statusLabel(value);
      },
      formatDate(value: Date): string {
        const date = new Date(value);
        const year = date.getFullYear();
        if (value == null || year == 1900) {
          return 'N/A';
        } else {
          return DateFormat.getDateLabel(value);
        }
      },
      newAttestation(): void {
      },
      exportTransactions(): void {
        // to implement
      },
      setExtended(extended: Extended): void {
        this.$emit('extended', extended);
      },
      openAttestation(e: Event, value: any) {
        this.section = new Section();
        this.section.component = SectionDemandeAttestation;
        this.section.type = 'attestation';
        this.section.data = storeAttestation.getAttestation(value.item.id);
        this.showSection = true;
      },
      closeSection(): void {
        this.$emit('closeDialog');
      },
      closeAttestation(): void {
        this.showSection = false;
        this.$emit('updated');
      }
    },
  });
</script>